import React, { useState } from 'react';
import { 
  Layout, 
  Card, 
  Tabs, 
  Typography, 
  Descriptions, 
  Space, 
  Tag,
  Button,
  Modal
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  InfoCircleOutlined,
  SettingOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons';
import { usePatientPortal } from '../context/PatientPortalContext';
import PatientPortalPasswordChange from '../components/PatientPortalPasswordChange';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const PatientPortalSettings = () => {
  const { patient, portalAccess, getDaysUntilExpiration } = usePatientPortal();
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExpirationStatus = () => {
    const daysUntil = getDaysUntilExpiration();
    if (daysUntil === null) return null;
    
    if (daysUntil <= 0) {
      return <Tag color="red">Expired</Tag>;
    } else if (daysUntil <= 7) {
      return <Tag color="orange">Expires in {daysUntil} days</Tag>;
    } else if (daysUntil <= 30) {
      return <Tag color="yellow">Expires in {daysUntil} days</Tag>;
    } else {
      return <Tag color="green">Active ({daysUntil} days remaining)</Tag>;
    }
  };

  return (
    <Content style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <SettingOutlined style={{ marginRight: 8 }} />
          Settings & Profile
        </Title>
        <Paragraph type="secondary">
          Manage your account settings and view your profile information.
        </Paragraph>
      </div>

      <Tabs defaultActiveKey="profile" size="large">
        <TabPane 
          tab={
            <span>
              <UserOutlined />
              Profile Information
            </span>
          } 
          key="profile"
        >
          <Card title="Personal Information" style={{ marginBottom: 24 }}>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Full Name" span={2}>
                <Text strong>
                  {patient?.firstName} {patient?.middleName ? `${patient.middleName} ` : ''}{patient?.lastName}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Date of Birth">
                <Space>
                  <CalendarOutlined />
                  {formatDate(patient?.dateOfBirth)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Sex">
                {patient?.sex || patient?.gender || 'Not specified'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <Space>
                  <MailOutlined />
                  {patient?.contactInfo?.email || 'Not provided'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                <Space>
                  <PhoneOutlined />
                  {patient?.contactInfo?.phone || 'Not provided'}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Portal Access Information">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Access Status">
                {getExpirationStatus()}
              </Descriptions.Item>
              <Descriptions.Item label="Access Expires">
                {formatDate(portalAccess?.expiresAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Login">
                {formatDateTime(portalAccess?.lastLoginAt)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <LockOutlined />
              Security
            </span>
          } 
          key="security"
        >
          <Card 
            title="Password Management"
            extra={
              <Button 
                type="primary" 
                icon={<LockOutlined />}
                onClick={() => setPasswordModalVisible(true)}
              >
                Change Password
              </Button>
            }
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text strong>Password Security</Text>
                <Paragraph type="secondary">
                  Keep your account secure by using a strong, unique password. 
                  We recommend changing your password regularly and never sharing it with others.
                </Paragraph>
              </div>

              <div>
                <Text strong>Security Requirements:</Text>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  <li>At least 8 characters long</li>
                  <li>Contains uppercase and lowercase letters</li>
                  <li>Contains at least one number</li>
                  <li>Contains at least one special character</li>
                </ul>
              </div>
            </Space>
          </Card>

          <Card title="Account Security Tips" style={{ marginTop: 24 }}>
            <Space direction="vertical" size="middle">
              <div>
                <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                <Text strong>Keep your login credentials secure</Text>
                <Paragraph type="secondary" style={{ marginLeft: 24, marginBottom: 8 }}>
                  Never share your email, phone number, or password with anyone. 
                  Healthcare providers will never ask for your password.
                </Paragraph>
              </div>

              <div>
                <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                <Text strong>Log out on shared devices</Text>
                <Paragraph type="secondary" style={{ marginLeft: 24, marginBottom: 8 }}>
                  Always log out when using public or shared computers to protect your health information.
                </Paragraph>
              </div>

              <div>
                <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                <Text strong>Report suspicious activity</Text>
                <Paragraph type="secondary" style={{ marginLeft: 24, marginBottom: 0 }}>
                  If you notice any unauthorized access or suspicious activity, 
                  contact your healthcare provider immediately.
                </Paragraph>
              </div>
            </Space>
          </Card>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <InfoCircleOutlined />
              Help & Support
            </span>
          } 
          key="help"
        >
          <Card title="Getting Help">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={4}>Contact Your Healthcare Provider</Title>
                <Paragraph>
                  If you need help with your portal access, have questions about your health information, 
                  or need to update your contact details, please contact your healthcare provider directly.
                </Paragraph>
              </div>

              <div>
                <Title level={4}>Technical Issues</Title>
                <Paragraph>
                  If you're experiencing technical difficulties with the portal:
                </Paragraph>
                <ul>
                  <li>Try refreshing the page or clearing your browser cache</li>
                  <li>Ensure you're using a supported browser (Chrome, Firefox, Safari, Edge)</li>
                  <li>Check your internet connection</li>
                  <li>Contact your healthcare provider's IT support if issues persist</li>
                </ul>
              </div>

              <div>
                <Title level={4}>Privacy & Security</Title>
                <Paragraph>
                  Your health information is protected and secure. This portal uses industry-standard 
                  encryption and security measures to protect your data. For more information about 
                  how your data is protected, please contact your healthcare provider.
                </Paragraph>
              </div>
            </Space>
          </Card>
        </TabPane>
      </Tabs>

      {/* Password Change Modal */}
      <Modal
        title="Change Password"
        visible={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        <PatientPortalPasswordChange />
      </Modal>
    </Content>
  );
};

export default PatientPortalSettings;
